"use client";

import { ArrowRight, Building2 } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Investing() {
  const eyebrow = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const headingLine1 = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const headingLine2 = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const paragraph = useRevealOnScroll<HTMLParagraphElement>({
    threshold: 0.1
  });
  const ctas = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const imageReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05
  });
  const chipReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05
  });
  return <section data-section-id="5288" id="investing-in-lasting-value" className="relative min-h-screen w-full overflow-hidden bg-background text-foreground">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-gradient-to-br from-background via-background to-accent" />
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.04] mix-blend-multiply" style={{
      backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")"
    }} />

      <div className="relative mx-auto max-w-[1440px] px-6 pt-24 pb-20 sm:px-10 md:pt-32 lg:px-16 lg:pt-40 lg:pb-28">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-10 xl:gap-16">
          {}
          <div className="order-2 flex flex-col justify-center lg:order-1 lg:col-span-7">
            {}
            <div ref={eyebrow.ref} className="mb-8 flex items-center gap-3 transition-all duration-700 ease-out" style={{
            opacity: eyebrow.isVisible ? 1 : 0,
            transform: eyebrow.isVisible ? "translateY(0)" : "translateY(12px)"
          }}>
              <span className="h-px w-8 bg-foreground/40" />
              <span className="font-default text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                Est. — SLI 2024
              </span>
            </div>

            {}
            <h1 className="font-default font-light text-foreground" style={{
            fontSize: "clamp(56px, 9vw, 144px)",
            lineHeight: 0.92,
            letterSpacing: "-0.025em"
          }}>
              <span ref={headingLine1.ref} className="block overflow-hidden">
                <span className="block transition-all duration-[900ms]" style={{
                transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
                transform: headingLine1.isVisible ? "translateY(0)" : "translateY(100%)",
                opacity: headingLine1.isVisible ? 1 : 0
              }}>
                  Architecting
                </span>
              </span>
              <span ref={headingLine2.ref} className="block overflow-hidden">
                <span className="block italic transition-all duration-[900ms]" style={{
                color: "hsl(43 45% 55%)",
                transitionDelay: "120ms",
                transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
                transform: headingLine2.isVisible ? "translateY(0)" : "translateY(100%)",
                opacity: headingLine2.isVisible ? 1 : 0
              }}>
                  lasting value.
                </span>
              </span>
            </h1>

            {}
            <p ref={paragraph.ref} className="font-default mt-10 max-w-[60ch] text-[18px] leading-relaxed text-muted-foreground transition-all duration-700" style={{
            opacity: paragraph.isVisible ? 1 : 0,
            transform: paragraph.isVisible ? "translateY(0)" : "translateY(16px)",
            transitionDelay: "400ms"
          }}>
              Summertime Lane Investments acquires residential properties with 
              integrity and vision. We transform houses into homes, fostering 
              long-term growth for our partners and the communities we serve.
            </p>

            {}
            <div ref={ctas.ref} className="mt-10 flex flex-col items-start gap-5 transition-all duration-700 sm:flex-row sm:items-center" style={{
            opacity: ctas.isVisible ? 1 : 0,
            transform: ctas.isVisible ? "translateY(0)" : "translateY(16px)",
            transitionDelay: "560ms"
          }}>
              <Link to="/HomePage">
                <Button size="lg" className="font-default h-14 rounded-sm bg-primary px-8 text-[15px] font-medium tracking-wide text-primary-foreground shadow-sm hover:bg-secondary hover:text-primary transition-all duration-200">
                  Our Investment Philosophy
                </Button>
              </Link>

              <Link to="/HomePage" className="font-default group inline-flex items-center gap-2 text-[15px] font-medium tracking-wide text-foreground">
                <span className="border-b-2 border-secondary pb-1 transition-colors group-hover:border-foreground">
                  Partner With Us
                </span>
                <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </div>

          {}
          <div className="order-1 lg:order-2 lg:col-span-5">
            <div ref={imageReveal.ref} className="relative transition-all duration-[1400ms] ease-out" style={{
            opacity: imageReveal.isVisible ? 1 : 0,
            transform: imageReveal.isVisible ? "scale(1)" : "scale(1.08)",
            transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)"
          }}>
              <div className="relative w-full overflow-hidden rounded-sm shadow-lg" style={{
              aspectRatio: "4 / 5"
            }}>
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723050/img/residential_investment_property.png" alt="A beautifully renovated residential property bathed in warm golden hour light, showcasing architectural integrity and curb appeal" className="h-full w-full object-cover" loading="lazy" decoding="async" />
                {}
                <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-primary/20 via-transparent to-transparent" />
              </div>

              {}
              <div ref={chipReveal.ref} className="absolute -bottom-6 -left-4 transition-all duration-700 sm:-left-6 md:-bottom-8 md:-left-8" style={{
              opacity: chipReveal.isVisible ? 1 : 0,
              transform: chipReveal.isVisible ? "translateY(0)" : "translateY(20px)",
              transitionDelay: "900ms"
            }}>
                <div className="flex items-center gap-3 rounded-sm bg-card px-4 py-3 shadow-md sm:px-5 sm:py-4 border-l-4 border-secondary">
                  <div className="flex size-9 items-center justify-center rounded-sm bg-accent">
                    <Building2 className="size-4 text-secondary" />
                  </div>
                  <div className="flex flex-col">
                    <span className="font-default text-[11px] uppercase tracking-[0.16em] text-muted-foreground" style={{
                    fontVariantNumeric: "tabular-nums"
                  }}>
                      Portfolio Focus
                    </span>
                    <span className="font-default text-[14px] italic text-foreground">
                      Residential · Renovation · Legacy
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {}
        <div className="mt-24 flex flex-col items-center gap-3 md:mt-32">
          <div className="relative h-12 w-px overflow-hidden bg-foreground/15">
            <span className="absolute left-0 top-0 h-6 w-px bg-secondary" style={{
            animation: "blazeScrollLine 2.2s cubic-bezier(0.65, 0, 0.35, 1) infinite"
          }} />
          </div>
          <span className="font-default text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            Scroll
          </span>
        </div>
      </div>

      <style>{`
        @keyframes blazeScrollLine {
          0% { transform: translateY(-100%); }
          60% { transform: translateY(200%); }
          100% { transform: translateY(200%); }
        }
      `}</style>
    </section>;
}